import React, {useState} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Grid, AccessibleGridCell, GridCellSummary, SelectedParentStorable} from 'constants/SampleConstants';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {isCellOccupiedAndAccessible} from 'samples/grids/GridCellsSelector';
import GridCellsSelectorContainer from 'samples/grids/GridCellsSelectorContainer';
import {ParentStorableSelector} from 'samples/ParentStorableDataSelector';
import {PlateSchemaType} from 'samples/storables/StorableSchemaConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';
import APIUtil from 'util/APIUtil';

interface AddSamplesFromBoxOrPlateModalProps {
  registryId: string | null;
  destinationColumnType: TableColumnType;
  destinationSchemaId: string | null;
  destinationSchemaName: string | null;
  onRequestClose: () => void;
  onRequestSubmit: (addSamplesCommand: {
    selectedParent: SelectedParentStorable;
    selectedCells: Array<GridCellSummary>;
  }) => void;
}

export default function AddSamplesFromBoxOrPlateModal(
  props: AddSamplesFromBoxOrPlateModalProps
): JSX.Element {
  const [selectedParent, setSelectedParent] = useState<SelectedParentStorable | undefined>(undefined);
  const [selectedCells, setSelectedCells] = useState<Array<GridCellSummary>>([]);

  const hasDestinationSchemaId = props.destinationSchemaId != null;
  const isContainerDestinationColumn = props.destinationColumnType === TableColumnType.CONTAINERS;
  const isPlateDestinationColumn = props.destinationColumnType === TableColumnType.PLATE;

  return (
    <Modal
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        props.onRequestSubmit({selectedParent: selectedParent!, selectedCells});
      }}
      bodyPadded={true}
      buttons={[
        {
          submit: true,
          text: 'Add',
          disabled: selectedParent == null || selectedCells.length === 0,
          style: ButtonStyle.SECONDARY,
        },
      ]}
      title={`Add containers from a ${isContainerDestinationColumn ? 'box or ' : ''}plate`}
    >
      <div className="u-small-spacing-top">
        <ParentStorableSelector
          autoFocus={true}
          isRequired={true}
          onRemove={() => {
            setSelectedParent(undefined);
          }}
          onSelect={(selectedParent) => {
            setSelectedParent(selectedParent);
            setSelectedCells(selectedParent.gridCells);
          }}
          parentStorableTypesOverride={[
            ModelTypeConstants.BOX,
            ModelTypeConstants.PLATE,
            ModelTypeConstants.LOCATION,
          ]}
          selectedParent={selectedParent}
          registryId={props.registryId}
        />
        {selectedParent != null &&
          (isContainerDestinationColumn
            ? APIUtil.isBox(selectedParent.storable!.id) || APIUtil.isPlate(selectedParent.storable!.id)
            : APIUtil.isPlate(selectedParent.storable!.id)) && (
            <GridCellsSelectorContainer
              gridId={selectedParent.storable!.id}
              allowSelectingCells={true}
              canSelectMultiple={true}
              checkCell={(grid: Grid, cell: GridCellSummary) => {
                let isIncorrectSchema = false;
                if (hasDestinationSchemaId) {
                  isIncorrectSchema = isContainerDestinationColumn
                    ? props.destinationSchemaId !== (cell as AccessibleGridCell).container?.containerSchemaId
                    : props.destinationSchemaId !== grid.storableSchema.id;
                } else if (isPlateDestinationColumn) {
                  isIncorrectSchema = !(
                    APIUtil.isPlateSchema(grid.storableSchema.id) &&
                    grid.storableSchema.plateSchemaType === PlateSchemaType.FIXED_PLATE
                  );
                }

                return isCellOccupiedAndAccessible(grid, cell) && !isIncorrectSchema;
              }}
              invalidCellMessage={`Must select non-empty${
                !isPlateDestinationColumn ? ', accessible' : ''
              } cell${
                hasDestinationSchemaId
                  ? isContainerDestinationColumn
                    ? ` with containers of schema type ${props.destinationSchemaName}`
                    : ` from plate of schema type ${props.destinationSchemaName}`
                  : isPlateDestinationColumn
                    ? ' from well plate'
                    : ''
              }`}
              isCompact={true}
              onSelectCells={setSelectedCells}
              selectedCells={selectedCells}
            />
          )}
      </div>
    </Modal>
  );
}
